<template>
  <div data-testid="accruals-data-ingestion-panel" class="accruals-data-ingestion-panel">
    <p
      v-if="loading"
      data-testid="accruals-data-ingestion-panel__loading"
      class="accruals-data-ingestion-panel__status"
    >
      Loading ingestion configuration…
    </p>

    <template v-else>
      <p
        v-if="!configured"
        data-testid="accruals-data-ingestion-panel__empty"
        class="accruals-data-ingestion-panel__banner"
      >
        {{ copy.emptyBanner }}
      </p>
      <p
        v-if="!canEdit"
        data-testid="accruals-data-ingestion-panel__readonly"
        class="accruals-data-ingestion-panel__banner"
      >
        {{ copy.readOnlyNotice }}
      </p>
      <p
        v-if="runInProgress"
        data-testid="accruals-data-ingestion-panel__run"
        class="accruals-data-ingestion-panel__banner"
      >
        {{ copy.runNotice }}
      </p>
      <p
        v-if="dirty && canEdit"
        data-testid="accruals-data-ingestion-panel__unsaved"
        class="accruals-data-ingestion-panel__banner"
      >
        Unsaved changes
      </p>

      <section class="accruals-data-ingestion-panel__section">
        <h2 class="accruals-data-ingestion-panel__section-title">Global settings</h2>

        <div class="accruals-data-ingestion-panel__grid">
          <div class="accruals-data-ingestion-panel__field">
            <label class="accruals-data-ingestion-panel__label">{{ copy.apHistoryLabel }}</label>
            <lb-number
              v-model="form.ap_history_months"
              label=""
              hidedetails
              allow-empty
              min="3"
              max="24"
              step="1"
              placeholder="3–24"
              :disabled="!canEdit"
              class="accruals-data-ingestion-panel__input"
              data-testid="accruals-data-ingestion-panel__ap-history"
            />
            <p class="accruals-data-ingestion-panel__hint">{{ copy.apHistoryHelp }}</p>
            <p v-if="showLowHistory" class="accruals-data-ingestion-panel__warn">{{ copy.lowHistoryWarning }}</p>
          </div>

          <div class="accruals-data-ingestion-panel__field">
            <label class="accruals-data-ingestion-panel__label">{{ copy.poMinLabel }}</label>
            <lb-number
              v-model="form.po_min_open_value_usd"
              label=""
              hidedetails
              allow-empty
              min="0"
              step="0.01"
              placeholder="1000"
              :disabled="!canEdit"
              class="accruals-data-ingestion-panel__input"
              data-testid="accruals-data-ingestion-panel__po-min"
            />
            <p class="accruals-data-ingestion-panel__hint">{{ copy.poMinHelp }}</p>
          </div>

          <div class="accruals-data-ingestion-panel__field">
            <label class="accruals-data-ingestion-panel__label">{{ copy.thresholdLabel }}</label>
            <lb-number
              v-model="thresholdPercent"
              label=""
              hidedetails
              allow-empty
              min="0"
              max="100"
              step="1"
              placeholder="0–100"
              :disabled="!canEdit"
              class="accruals-data-ingestion-panel__input"
              data-testid="accruals-data-ingestion-panel__threshold"
            />
            <p class="accruals-data-ingestion-panel__hint">{{ copy.thresholdHelp }} {{ thresholdZoneLabel }}</p>
            <p v-if="showHighThreshold" class="accruals-data-ingestion-panel__hint">{{ copy.highThresholdNote }}</p>
            <p v-if="showLowThreshold" class="accruals-data-ingestion-panel__hint">{{ copy.lowThresholdNote }}</p>
          </div>

          <div class="accruals-data-ingestion-panel__field">
            <label class="accruals-data-ingestion-panel__label">{{ copy.triggerLabel }}</label>
            <lb-number
              v-model="form.period_end_trigger"
              label=""
              hidedetails
              allow-empty
              min="0"
              step="1"
              :placeholder="copy.triggerPlaceholder"
              :disabled="!canEdit"
              class="accruals-data-ingestion-panel__input"
              data-testid="accruals-data-ingestion-panel__trigger"
            />
            <p class="accruals-data-ingestion-panel__hint">{{ copy.triggerHelp }}</p>
            <p v-if="periodEndTriggerHintText" class="accruals-data-ingestion-panel__hint">
              {{ periodEndTriggerHintText }}
            </p>
          </div>
        </div>

        <div class="accruals-data-ingestion-panel__switch-row">
          <div class="accruals-data-ingestion-panel__switch">
            <span class="accruals-data-ingestion-panel__label">{{ copy.nonPoLabel }}</span>
            <base-switch
              :value="includeNonPo"
              :disabled="!canEdit"
              :aria-label="copy.nonPoLabel"
              data-testid="accruals-data-ingestion-panel__non-po"
              @input="onNonPo"
            />
          </div>
          <p class="accruals-data-ingestion-panel__hint">{{ copy.nonPoHelp }}</p>
        </div>

        <div class="accruals-data-ingestion-panel__switch-row">
          <div class="accruals-data-ingestion-panel__switch">
            <span class="accruals-data-ingestion-panel__label">{{ copy.poClaimGrainLabel }}</span>
            <base-switch
              :value="pickAtPoLine"
              :disabled="!canEdit"
              :aria-label="copy.poClaimGrainLabel"
              data-testid="accruals-data-ingestion-panel__po-claim-grain"
              @input="onPoClaimGrain"
            />
          </div>
          <p class="accruals-data-ingestion-panel__hint">{{ copy.poClaimGrainHelp }}</p>
        </div>
      </section>

      <section
        v-for="facet in visibleFacets"
        :key="facet.key"
        class="accruals-data-ingestion-panel__section"
        :data-testid="`accruals-data-ingestion-panel__facet--${facet.key}`"
      >
        <h2 class="accruals-data-ingestion-panel__section-title">{{ facet.label }}</h2>
        <p class="accruals-data-ingestion-panel__hint">{{ copy.facetSource }}: {{ facet.source }}</p>
        <p class="accruals-data-ingestion-panel__hint">{{ copy.facetPullType }}: {{ facet.pull_type }}</p>
        <p class="accruals-data-ingestion-panel__hint">{{ facet.effective_window }}</p>
      </section>

      <div class="accruals-data-ingestion-panel__actions">
        <base-button
          v-if="canEdit"
          primary
          small
          class="shadow-off"
          label="Save"
          :disabled="saving || !dirty"
          :loading="saving"
          data-testid="accruals-data-ingestion-panel__save"
          @click="onSave"
        />
      </div>

      <p v-if="error" data-testid="accruals-data-ingestion-panel__error" class="accruals-data-ingestion-panel__warn">
        {{ error }}
      </p>
    </template>
  </div>
</template>

<script>
import {
  ADMIN_CONSOLE_FC_RIGHT,
  ADMIN_CONSOLE_INGESTION_COPY,
  ADMIN_CONSOLE_INGESTION_RIGHT,
} from '@/modules/admin/utils/adminConsoleConstants';
import {
  DEFAULT_AUTO_POST_THRESHOLD,
  emptyIngestionForm,
  formFromConfig,
  formSnapshot,
  integerDays,
  percentFromThreshold,
  periodEndTriggerHint,
  previewFacets,
  thresholdFromPercent,
  thresholdZone,
} from './accrualsDataIngestionUtils';

export default {
  name: 'AccrualsDataIngestionPanel',
  data() {
    return {
      loading: true,
      saving: false,
      configured: false,
      canEdit: false,
      runInProgress: false,
      form: emptyIngestionForm(),
      savedSnapshot: '',
      facets: [],
      error: '',
      copy: ADMIN_CONSOLE_INGESTION_COPY,
    };
  },
  computed: {
    dirty() {
      return formSnapshot(this.form) !== this.savedSnapshot;
    },
    thresholdPercent: {
      get() {
        return percentFromThreshold(this.form.accrual_auto_post_threshold);
      },
      set(raw) {
        this.form.accrual_auto_post_threshold = thresholdFromPercent(raw);
      },
    },
    thresholdZoneLabel() {
      return thresholdZone(this.form.accrual_auto_post_threshold);
    },
    showLowHistory() {
      const months = Number(this.form.ap_history_months);
      return this.form.ap_history_months != null && this.form.ap_history_months !== '' && months < 6;
    },
    showHighThreshold() {
      const value = Number(this.form.accrual_auto_post_threshold);
      return this.form.accrual_auto_post_threshold != null && this.form.accrual_auto_post_threshold !== '' && value > 0.9;
    },
    showLowThreshold() {
      const value = Number(this.form.accrual_auto_post_threshold);
      return this.form.accrual_auto_post_threshold != null && this.form.accrual_auto_post_threshold !== '' && value < 0.5;
    },
    includeNonPo() {
      return this.form.include_non_po_invoices === true;
    },
    pickAtPoLine() {
      return this.form.po_claim_grain === 1;
    },
    periodEndTriggerHintText() {
      return periodEndTriggerHint(this.form.period_end_trigger);
    },
    visibleFacets() {
      return previewFacets(this.facets, this.form.ap_history_months);
    },
    canMutateIngestion() {
      return (
        this.$nova.hasRight(ADMIN_CONSOLE_INGESTION_RIGHT, ['add', 'edit'])
        || this.$nova.hasRight(ADMIN_CONSOLE_FC_RIGHT, 'admin')
      );
    },
  },
  mounted() {
    this.load();
    window.addEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    this.form = emptyIngestionForm();
    this.error = '';
  },
  methods: {
    onBeforeUnload(event) {
      if (!this.dirty) return;
      event.preventDefault();
      event.returnValue = this.copy.leavePrompt;
    },
    confirmLeave() {
      if (!this.dirty) return true;
      return window.confirm(this.copy.leavePrompt);
    },
    applyPayload(data) {
      this.configured = Boolean(data?.configured);
      this.canEdit = data?.can_edit === true || this.canMutateIngestion;
      this.runInProgress = Boolean(data?.run_in_progress);
      this.facets = data?.facets || [];
      this.form = formFromConfig(data?.config);
      this.savedSnapshot = formSnapshot(this.form);
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const data = await this.$store.dispatch('accruals/loadIngestionConfig');
        this.applyPayload(data);
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Failed to load ingestion configuration';
        this.canEdit = this.canMutateIngestion;
        console.error('[AccrualsDataIngestionPanel]: load', err);
      } finally {
        this.loading = false;
      }
    },
    onNonPo(value) {
      this.form.include_non_po_invoices = value === true;
    },
    onPoClaimGrain(value) {
      this.form.po_claim_grain = value === true ? 1 : 0;
    },
    async onSave() {
      this.saving = true;
      this.error = '';
      try {
        const data = await this.$store.dispatch('accruals/saveIngestionConfig', {
          ...this.form,
          ap_history_months: this.form.ap_history_months == null || this.form.ap_history_months === ''
            ? null
            : Number(this.form.ap_history_months),
          po_min_open_value_usd: this.form.po_min_open_value_usd == null || this.form.po_min_open_value_usd === ''
            ? null
            : Number(this.form.po_min_open_value_usd),
          accrual_auto_post_threshold:
            this.form.accrual_auto_post_threshold == null || this.form.accrual_auto_post_threshold === ''
              ? DEFAULT_AUTO_POST_THRESHOLD
              : Number(this.form.accrual_auto_post_threshold),
          period_end_trigger: integerDays(this.form.period_end_trigger),
          include_non_po_invoices: this.form.include_non_po_invoices === true,
        });
        this.applyPayload(data);
      } catch (err) {
        const details = err?.response?.data?.data;
        const detailText = Array.isArray(details) ? details.join(' ') : '';
        this.error = [err?.response?.data?.message || err?.message || 'Save failed', detailText].filter(Boolean).join(' ');
        console.error('[AccrualsDataIngestionPanel]: save', err);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped src="./AccrualsDataIngestionPanel.scss"></style>
